import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RecipeSuggestionCard = ({ recipe, onSave }) => {
  if (!recipe) return null;

  const formatInstructions = (instructions) => {
    if (!instructions) return [];
    return instructions?.split('\n')?.filter(step => step?.trim());
  };

  const getDietaryBadgeColor = (restriction) => {
    const colors = {
      'vegetarian': 'bg-green-100 text-green-700',
      'vegan': 'bg-green-100 text-green-700',
      'gluten-free': 'bg-blue-100 text-blue-700',
      'dairy-free': 'bg-purple-100 text-purple-700',
      'keto': 'bg-orange-100 text-orange-700',
      'low-carb': 'bg-red-100 text-red-700',
    };
    return colors?.[restriction?.toLowerCase()] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="bg-card rounded-lg border p-6 hover:shadow-lg transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-foreground mb-2">
            {recipe?.name}
          </h3>
          <div className="flex items-center space-x-4 text-sm text-muted-foreground">
            <div className="flex items-center">
              <Icon name="Clock" size={14} className="mr-1" />
              {recipe?.prepTime} min
            </div>
            <div className="flex items-center">
              <Icon name="Users" size={14} className="mr-1" />
              {recipe?.servings} servings
            </div>
            {recipe?.cuisine && (
              <div className="flex items-center">
                <Icon name="MapPin" size={14} className="mr-1" />
                {recipe?.cuisine}
              </div>
            )}
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onSave?.(recipe)}
        >
          <Icon name="Bookmark" size={14} className="mr-1" />
          Save
        </Button>
      </div>
      {/* Dietary Restrictions */}
      {recipe?.dietaryRestrictions?.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {recipe?.dietaryRestrictions?.map((restriction, index) => (
            <span
              key={index}
              className={`px-2 py-1 text-xs rounded-full font-medium ${getDietaryBadgeColor(restriction)}`}
            >
              {restriction}
            </span>
          ))}
        </div>
      )}
      {/* Ingredients */}
      <div className="mb-4">
        <h4 className="font-medium text-foreground mb-2 flex items-center">
          <Icon name="ShoppingCart" size={16} className="mr-2" />
          Ingredients
        </h4>
        <div className="grid grid-cols-1 gap-1">
          {recipe?.ingredients?.slice(0, 6)?.map((ingredient, index) => (
            <div key={index} className="flex items-center text-sm text-muted-foreground">
              <Icon name="Minus" size={12} className="mr-2 text-primary" />
              {ingredient}
            </div>
          ))}
          {recipe?.ingredients?.length > 6 && (
            <div className="text-sm text-muted-foreground italic">
              +{recipe?.ingredients?.length - 6} more ingredients
            </div>
          )}
        </div>
      </div>
      {/* Instructions Preview */}
      <div className="mb-4">
        <h4 className="font-medium text-foreground mb-2 flex items-center">
          <Icon name="List" size={16} className="mr-2" />
          Instructions
        </h4>
        <div className="space-y-1">
          {formatInstructions(recipe?.instructions)?.slice(0, 3)?.map((step, index) => (
            <div key={index} className="flex items-start text-sm text-muted-foreground">
              <span className="bg-primary/10 text-primary text-xs rounded-full w-5 h-5 flex items-center justify-center mr-2 mt-0.5 font-medium">
                {index + 1}
              </span>
              <span className="flex-1 leading-relaxed">
                {step?.length > 80 ? `${step?.substring(0, 80)}...` : step}
              </span>
            </div>
          ))}
          {formatInstructions(recipe?.instructions)?.length > 3 && (
            <div className="text-sm text-muted-foreground italic pl-7">
              +{formatInstructions(recipe?.instructions)?.length - 3} more steps
            </div>
          )}
        </div>
      </div>
      {/* Actions */}
      <div className="flex space-x-2 pt-4 border-t">
        <Button variant="outline" size="sm" className="flex-1">
          <Icon name="Eye" size={14} className="mr-1" />
          View Full Recipe
        </Button>
        <Button variant="outline" size="sm" className="flex-1">
          <Icon name="Plus" size={14} className="mr-1" />
          Add to Menu
        </Button>
      </div>
    </div>
  );
};

export default RecipeSuggestionCard;